<script setup lang="ts">

import dayjs from 'dayjs';
import { ref, computed } from 'vue';
import { useRouter } from '@library/Services';
import { useNotify } from '@library/Components';
import { useUserSettings } from '@services/UserSettings';
import { useVehicleApi, useViewModelInstance } from '@app/MyGarage';

const model = await useViewModelInstance();
const odometer = ref( model.vehicle.odometer ?? 0 );

const lastOdometerString = ( model.vehicle.odometer ?? 0 ).toString().padStart( 7, '0' ) + '0';
const odometerString = computed( () => odometer.value.toString().padStart( 7, '0' ) + '0' );
const lastOdometerLength = ( model.vehicle.odometer ?? 0 ).toString().length;

/**
 * Acknowedge the cover
 */
async function setOdometer()
{
    
    const api = useVehicleApi();
    const result = await api.updateOdometer( odometer.value ); 
    
    if( result.success == false )
    {
        await useNotify( "Unable to update vehicle odometer.  Close the app and try again, and if they problem persists, contact your dealership for assistance.", "Update Odometer" )
        return;
    }
    
    await useNotify( "Odometer has been updated", "Update Odometer" )

    const router = useRouter();
    router.go( '/my-garage' );

}

/**
 * Skip the check for a day
 */
async function skip()
{
    
    const settings = useUserSettings();
    settings.skipUpdateOdometerReminderUntil = dayjs().add( 1, 'day' ).toISOString();

    await useNotify( "You will be reminded again tomorrow", "Update Odometer" )

    const router = useRouter();
    router.go( '/my-garage' );

}

</script>

<template>

    <div class="flex flex-col h-full pt-6 pb-24 px-4 3xl:px-6 overflow-y-auto
                bg-gradient-to-b from-gray-900 to-gray-900 via-gray-850 via-60%">
        
        <div class="flex flex-col justify-center items-center">

            <!-- VISUALISATION -->
            <div clas="flex flex-col w-full">
                <h1 class="text-sm 3xl:text-2xl uppercase bg-black/50 py-0.5 3xl:py-1 px-3 rounded-t-lg opacity-60 -top-px">
                    Last Odometer Reading
                </h1>
            </div>
            <div class="bg-black rounded-2xl 3xl:rounded-3xl border-8 border-black overflow-hidden
                        font-mono opacity-60
                        flex justify-end gap-0.5 3xl:gap-2
                        mb-6 3xl:mb-10 ring-1 ring-white/20">
                <div v-for="(c, index) in lastOdometerString"
                     class="bg-gradient-to-b from-white/[12%] via-black to-white/[15%]
                            last:from-white/70 last:via-white/50 last:to-white/90 last:text-black
                            text-3xl lg:text-5xl 3xl:text-[3rem]
                            px-2 py-1 lg:py-2 3xl:px-3"
                     :class="{
                        'text-2xl lg:text-[2rem] 3xl:text-[2.85rem]': lastOdometerString.length > 6,
                        '!text-white/50': index < 7 - lastOdometerLength
                     }">
                    {{ c }}
                </div>
            </div>
            
            <h1 class="text-sm 3xl:text-2xl uppercase bg-black/50 py-0.5 3xl:py-1 px-3 rounded-t-lg -top-px">
                New Odometer Reading
            </h1>
            <div class="bg-black rounded-2xl 3xl:rounded-3xl border-8 border-black overflow-hidden
                        font-mono
                        flex justify-end gap-0.5 3xl:gap-2
                        mb-6 3xl:mb-10 ring-1 ring-white/10">
                <div v-for="(c, index) in odometerString"
                     class="bg-gradient-to-b from-white/[12%] via-black to-white/[15%]
                            last:from-white/70 last:via-white/50 last:to-white/90 last:text-black
                            text-3xl lg:text-5xl 3xl:text-[3rem]
                            px-2 py-1 lg:py-2 3xl:px-3"
                     :class="{
                        'text-2xl lg:text-[2rem] 3xl:text-[2.85rem]': odometerString.length > 6,
                        '!text-white/50': index < 7 - odometer.toString().length
                     }">
                    {{ c }}
                </div>
            </div>

            <div class="bg-white/5 rounded-lg 3xl:rounded-xl p-6 3xl:p-8 pt-3 3xl:pt-5 flex flex-col w-full">
            
                <!-- ODOMETER INPUT -->
                <div class="flex flex-col text-center w-full pb-3">
                    <h2 class="text-base lg:text-lg 3xl:text-2xl text-white tracking-wider text-center mb-3 3xl:mb-5">
                        It's time to update your odometer
                    </h2>
                    <p class="text-sm 3xl:text-lg opacity-60">Please read the KM's off your dashboard<br/>and enter them below</p>
                </div>
                <div class="w-full 3xl:py-2 px-2 3xl:px-4 bg-white/[7%] rounded">
            
                    <NumberTextField 
                        v-model="odometer"
                        label="Odometer"
                        bg-color="transparent"
                        hide-details="auto"
                        variant="solo"
                        autofocus
                        required
                        flat
                        maxlength="8"
                        max="2000000"
                        :min="model.vehicle.odometer ?? 0"
                        :interval="500"
                        class="w-full"
                        @keypress.enter="() => setOdometer()">
                        <template #append-inner>km</template>
                    </NumberTextField>
                
                </div>

                <Button 
                    :variant="model.vehicle.odometer == odometer ? 'outlined' : 'flat'"
                    color="secondary"
                    class="!h-14 lg:!h-16 3xl:!h-[4.5rem] mt-3 lg:mt-4 3xl:mt-6 3xl:text-xl"
                    :disabled="model.vehicle.odometer == odometer"
                    @click="() => setOdometer()">
                    Update Odometer
                </Button>

                <p class="text-sm 3xl:text-lg opacity-60 text-center mt-2 3xl:mt-4">
                    If you don't have access to your car<br/>try to provide a good estimate
                </p>

            </div>

            <Button 
                variant="text"
                color="white"
                class="!h-11 lg:!h-12 3xl:!h-14 mt-3 lg:mt-4 3xl:mt-6 3xl:text-xl"
                @click="() => skip()">
                Skip for now
            </Button>
                
        </div>
            
    </div>
    
</template>